import React, { useContext, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { CartContext } from "../context/CartProvider"; // Ensure this path is correct
import { BsCartX } from "react-icons/bs"; // Empty cart icon
import {
  FiTrash2,
  FiMinusCircle,
  FiPlusCircle,
  FiArrowRight,
  FiGift,
  FiLoader,
  FiXCircle,
} from "react-icons/fi"; // Icons
import axios from "axios";
import { toast } from "react-toastify"; // Using react-toastify

// Helper to get the display price
const getItemDisplayPrice = (item) => {
  return parseFloat(item.price || 0);
};

const Cart = () => {
  const { cart, increment, decrement, removeFromCart, clearCart } =
    useContext(CartContext);
  const [couponCodeInput, setCouponCodeInput] = useState(""); // For the input field

  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [couponMessage, setCouponMessage] = useState({ text: "", type: "" });

  const baseUrl = process.env.REACT_APP_API_URL;

  // Calculate subtotal based on cart items
  const subtotal = cart.reduce(
    (total, item) => total + getItemDisplayPrice(item) * item.quantity,
    0
  );

  // Determine the final total to display
  const displayTotal = appliedCoupon ? appliedCoupon.finalAmount : subtotal;

  // Function to handle coupon application (NO CHANGES)
  const handleApplyCoupon = async () => {
    if (!couponCodeInput.trim()) {
      setCouponMessage({ text: "Please enter a coupon code.", type: "error" });
      toast.warn("Please enter a coupon code.");
      return;
    }
    setIsApplyingCoupon(true);
    setCouponMessage({ text: "", type: "" });
    try {
      const response = await axios.post(`${baseUrl}coupon/apply`, {
        couponCode: couponCodeInput,
        cartTotal: subtotal,
      });

      if (response.data && response.data.status === 1) {
        setAppliedCoupon({
          code: couponCodeInput.toUpperCase(),
          discountAmount: parseFloat(response.data.discountAmount),
          finalAmount: parseFloat(response.data.finalAmount),
          message: response.data.message,
        });
        setCouponMessage({ text: response.data.message, type: "success" });
        toast.success(response.data.message);
      } else {
        setAppliedCoupon(null);
        setCouponMessage({
          text: response.data.message || "Invalid or expired coupon.",
          type: "error",
        });
        toast.error(response.data.message || "Invalid coupon code.");
      }
    } catch (error) {
      console.error("Error applying coupon:", error);
      setAppliedCoupon(null);
      const errorMessage =
        error.response?.data?.message ||
        "Could not apply coupon. Please try again.";
      setCouponMessage({ text: errorMessage, type: "error" });
      toast.error(errorMessage);
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCodeInput("");
    setCouponMessage({ text: "Coupon removed.", type: "info" });
    toast.info("Coupon removed.");
  };

  useEffect(() => {
    if (appliedCoupon && cart.length === 0) {
      handleRemoveCoupon();
    }
  }, [cart, subtotal]);

  // STYLES (NO CHANGES)
  const gradientButtonStyle = {
    backgroundImage: "linear-gradient(to right, #e5272e, #f59332)",
    color: "white",
    border: "none",
    padding: "10px 25px",
    borderRadius: "8px",
    fontWeight: "bold",
    fontSize: "1rem",
    cursor: "pointer",
    transition: "opacity 0.2s ease, transform 0.1s ease",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    textDecoration: "none",
  };

  const primaryButtonStyle = {
    backgroundColor: "#0f4372",
    color: "#fff",
    border: "none",
    padding: "0px 15px",
    borderRadius: "0 6px 6px 0",
    fontWeight: "500",
    cursor: "pointer",
    transition: "background-color 0.2s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "calc(1.5em + .5rem + 2px)",
  };
  const couponInputStyle = {
    borderRadius: "6px 0 0 6px",
  };

  return (
    <section className="py-5" style={{ backgroundColor: "#f4f7f6" }}>
      <div className="container">
        <div className="text-center mb-5">
          <h1 className="fw-bold" style={{ color: "#1A202C" }}>
            Your Shopping Cart
          </h1>
          {cart.length > 0 && (
            <p className="text-muted">
              Review your items and proceed to checkout.
            </p>
          )}
        </div>

        {cart.length === 0 ? (
          <div className="text-center py-5 bg-white shadow-sm rounded-3">
            <BsCartX size={80} className="mb-4" style={{ color: "#e5272e" }} />
            <h4 className="mb-3" style={{ color: "#333" }}>
              Your Cart is Empty
            </h4>
            <p className="text-muted mb-4">
              Looks like you haven't added anything yet. <br /> Explore our
              products and find something you love!
            </p>
            <Link to="/shop" style={gradientButtonStyle}>
              Continue Shopping{" "}
              <FiArrowRight size={20} style={{ marginLeft: "8px" }} />
            </Link>
          </div>
        ) : (
          <div className="row gx-lg-5">
            {/* Cart Items Column */}
            <div className="col-lg-8">
              <div className="card border-0 shadow-sm rounded-3 mb-4">
                <div className="card-header bg-light py-3">
                  <h5 className="mb-0 fw-medium" style={{ color: "#0f4372" }}>
                    Cart Items ({cart.length})
                  </h5>
                </div>
                <div className="card-body p-lg-0">
                  {/* --- DESKTOP VIEW: TABLE (Visible on lg screens and up) --- */}
                  <div className="table-responsive d-none d-lg-block">
                    <table className="table align-middle mb-0">
                      <thead>
                        <tr style={{ borderBottom: "2px solid #dee2e6" }}>
                          <th
                            scope="col"
                            className="py-3 ps-3"
                            style={{ width: "50%" }}
                          >
                            Product
                          </th>
                          <th scope="col" className="py-3 text-center">
                            Price
                          </th>
                          <th
                            scope="col"
                            className="py-3 text-center"
                            style={{ minWidth: "150px" }}
                          >
                            Quantity
                          </th>
                          <th scope="col" className="py-3 text-center">
                            Subtotal
                          </th>
                          <th scope="col" className="py-3 pe-3 text-center">
                            Remove
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {cart.map((item) => {
                          const itemPrice = getItemDisplayPrice(item);
                          const itemIdentifier = item.product_id || item.id;
                          return (
                            <tr
                              key={itemIdentifier}
                              style={{ borderBottom: "1px solid #e9ecef" }}
                            >
                              <td className="py-3 ps-3">
                                <Link
                                  to={`/product_details/${item.product_id}`}
                                  className="text-decoration-none"
                                  style={{
                                    color: "#2c3e50",
                                    fontWeight: "500",
                                  }}
                                >
                                  <div className="d-flex align-items-center">
                                    <img
                                      src={
                                        item.productImage ||
                                        `https://placehold.co/70x70/E9ECEF/333?text=${
                                          item.product_name
                                            ? item.product_name.substring(0, 3)
                                            : "Img"
                                        }`
                                      }
                                      alt={item.product_name}
                                      style={{
                                        width: "70px",
                                        height: "70px",
                                        objectFit: "cover",
                                        borderRadius: "8px",
                                        marginRight: "15px",
                                      }}
                                    />
                                    <div>{item.product_name}</div>
                                  </div>
                                </Link>
                              </td>
                              <td className="py-3 text-center fw-medium">
                                £{itemPrice.toFixed(2)}
                              </td>
                              <td className="py-3 text-center">
                                <div className="d-flex justify-content-center align-items-center">
                                  <button
                                    onClick={() => decrement(itemIdentifier)}
                                    className="btn btn-sm p-0 lh-1"
                                    title="Decrease quantity"
                                    style={{
                                      color: "#e5272e",
                                      background: "transparent",
                                    }}
                                  >
                                    <FiMinusCircle size={22} />
                                  </button>
                                  <span
                                    className="mx-3 fw-medium"
                                    style={{ minWidth: "20px" }}
                                  >
                                    {item.quantity}
                                  </span>
                                  <button
                                    onClick={() => increment(itemIdentifier)}
                                    className="btn btn-sm p-0 lh-1"
                                    title="Increase quantity"
                                    style={{
                                      color: "#0f4372",
                                      background: "transparent",
                                    }}
                                  >
                                    <FiPlusCircle size={22} />
                                  </button>
                                </div>
                              </td>
                              <td
                                className="py-3 text-center fw-bold"
                                style={{ color: "#0f4372" }}
                              >
                                £{(itemPrice * item.quantity).toFixed(2)}
                              </td>
                              <td className="py-3 pe-3 text-center">
                                <button
                                  onClick={() => removeFromCart(itemIdentifier)}
                                  className="btn btn-sm p-0"
                                  title="Remove item"
                                  style={{
                                    color: "#dc3545",
                                    background: "transparent",
                                  }}
                                >
                                  <FiTrash2 size={20} />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* --- MOBILE VIEW: CARDS (Hidden on lg screens and up) --- */}
                  <div className="d-lg-none p-3">
                    {cart.map((item) => {
                      const itemPrice = getItemDisplayPrice(item);
                      const itemIdentifier = item.product_id || item.id;
                      return (
                        <div
                          key={itemIdentifier}
                          className="mb-3 p-3 border rounded-3 bg-white"
                        >
                          <div className="row g-3">
                            {/* Product Info */}
                            <div className="col-12 d-flex">
                              <Link to={`/product_details/${item.product_id}`}>
                                <img
                                  src={
                                    item.productImage ||
                                    `https://placehold.co/80x80/E9ECEF/333?text=${
                                      item.product_name
                                        ? item.product_name.substring(0, 3)
                                        : "Img"
                                    }`
                                  }
                                  alt={item.product_name}
                                  style={{
                                    width: "80px",
                                    height: "80px",
                                    objectFit: "cover",
                                    borderRadius: "8px",
                                  }}
                                />
                              </Link>
                              <div className="ms-3">
                                <Link
                                  to={`/product_details/${item.product_id}`}
                                  className="text-decoration-none"
                                  style={{
                                    color: "#2c3e50",
                                    fontWeight: "600",
                                  }}
                                >
                                  {item.product_name}
                                </Link>
                                <div className="text-muted small mt-1">
                                  Price: £{itemPrice.toFixed(2)}
                                </div>
                              </div>
                            </div>

                            {/* Quantity and Subtotal */}
                            <div className="col-12 d-flex justify-content-between align-items-center">
                              <div className="d-flex align-items-center">
                                <button
                                  onClick={() => decrement(itemIdentifier)}
                                  className="btn btn-sm p-0 lh-1"
                                  style={{
                                    color: "#e5272e",
                                    background: "transparent",
                                  }}
                                >
                                  <FiMinusCircle size={24} />
                                </button>
                                <span className="mx-3 fw-bold fs-6">
                                  {item.quantity}
                                </span>
                                <button
                                  onClick={() => increment(itemIdentifier)}
                                  className="btn btn-sm p-0 lh-1"
                                  style={{
                                    color: "#0f4372",
                                    background: "transparent",
                                  }}
                                >
                                  <FiPlusCircle size={24} />
                                </button>
                              </div>
                              <div className="text-end">
                                <span
                                  className="fw-bold fs-6"
                                  style={{ color: "#0f4372" }}
                                >
                                  £{(itemPrice * item.quantity).toFixed(2)}
                                </span>
                              </div>
                            </div>

                            {/* Remove Button */}
                            <div className="col-12 text-end">
                              <button
                                onClick={() => removeFromCart(itemIdentifier)}
                                className="btn btn-sm btn-outline-danger py-1 px-2"
                              >
                                <FiTrash2 size={16} className="me-1" /> Remove
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                {cart.length > 0 && typeof clearCart === "function" && (
                  <div className="card-footer bg-transparent text-end py-3 border-top-0">
                    <button
                      onClick={() => {
                        clearCart();
                        handleRemoveCoupon();
                      }}
                      className="btn btn-sm btn-outline-danger"
                    >
                      Clear Cart
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Cart Summary Column (This will stack automatically on mobile) */}
            <div className="col-lg-4">
              <div
                className="card border-0 shadow-sm rounded-3"
                style={{ position: "sticky", top: "20px" }}
              >
                <div className="card-header bg-light py-3">
                  <h5 className="mb-0 fw-medium" style={{ color: "#0f4372" }}>
                    Order Summary
                  </h5>
                </div>
                <div className="card-body p-4">
                  <div className="mb-3">
                    <label
                      htmlFor="couponCodeInput"
                      className="form-label small text-muted"
                    >
                      Have a Coupon Code?
                    </label>
                    <div className="input-group">
                      <input
                        type="text"
                        value={couponCodeInput}
                        onChange={(e) =>
                          setCouponCodeInput(e.target.value.toUpperCase())
                        }
                        id="couponCodeInput"
                        className="form-control form-control-sm"
                        placeholder="Enter coupon"
                        disabled={isApplyingCoupon || !!appliedCoupon}
                        style={couponInputStyle}
                      />
                      {!appliedCoupon ? (
                        <button
                          className="btn btn-sm"
                          style={primaryButtonStyle}
                          onClick={handleApplyCoupon}
                          disabled={isApplyingCoupon}
                        >
                          {isApplyingCoupon ? (
                            <FiLoader
                              className="spinner"
                              size={16}
                              style={{ marginRight: "5px" }}
                            />
                          ) : (
                            <FiGift size={16} style={{ marginRight: "5px" }} />
                          )}
                          {isApplyingCoupon ? "Applying..." : "Apply"}
                        </button>
                      ) : (
                        <button
                          className="btn btn-sm btn-danger"
                          style={{
                            ...primaryButtonStyle,
                            backgroundColor: "#dc3545",
                            padding: "0px 10px",
                          }}
                          onClick={handleRemoveCoupon}
                          title="Remove Coupon"
                        >
                          <FiXCircle size={16} style={{ marginRight: "5px" }} />{" "}
                          Remove
                        </button>
                      )}
                    </div>
                    {couponMessage.text && (
                      <div
                        className={`small mt-2 ${
                          couponMessage.type === "success"
                            ? "text-success"
                            : couponMessage.type === "error"
                            ? "text-danger"
                            : "text-muted"
                        }`}
                      >
                        {couponMessage.text}
                      </div>
                    )}
                  </div>
                  <hr />

                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Subtotal:</span>
                    <span
                      className="fw-medium"
                      style={{
                        textDecoration: appliedCoupon ? "line-through" : "none",
                        color: appliedCoupon ? "#6c757d" : "#212529",
                      }}
                    >
                      £{subtotal.toFixed(2)}
                    </span>
                  </div>

                  {appliedCoupon && (
                    <div className="d-flex justify-content-between mb-2 text-success">
                      <span>Coupon ({appliedCoupon.code}):</span>
                      <span className="fw-medium">
                        - £{appliedCoupon.discountAmount.toFixed(2)}
                      </span>
                    </div>
                  )}

                  <div className="d-flex justify-content-between mb-1">
                    <span className="text-muted">Shipping:</span>
                    <span className="fw-medium">Calculated at Checkout</span>
                  </div>
                  <hr />
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0 fw-bold" style={{ color: "#1A202C" }}>
                      Total:
                    </h5>
                    <h5 className="mb-0 fw-bold" style={{ color: "#e5272e" }}>
                      £{displayTotal.toFixed(2)}
                    </h5>
                  </div>

                  <Link
                    to={cart.length > 0 ? "/checkout" : "/cart"}
                    state={{
                      product: cart,
                      appliedCouponData: appliedCoupon,
                      finalTotalAmount: displayTotal,
                    }}
                    className="w-100 d-block text-decoration-none"
                  >
                    <button
                      type="button"
                      className="w-100"
                      style={{
                        ...gradientButtonStyle,
                        padding: "12px 25px",
                        fontSize: "1.1rem",
                      }}
                      disabled={cart.length < 1}
                    >
                      Proceed to Checkout{" "}
                      <FiArrowRight size={20} style={{ marginLeft: "8px" }} />
                    </button>
                  </Link>
                  <Link
                    to="/shop"
                    className="btn btn-sm btn-outline-secondary w-100 mt-3"
                  >
                    Continue Shopping
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <style jsx global>{`
        .spinner {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </section>
  );
};

export default Cart;
